import { useState } from 'react';
import { useNavigate } from 'react-router';
import { css, styled } from 'styled-components';

import { Avatar } from '#/components/Avatar/Avatar';
import { Box } from '#/components/Box/Box';
import { Button } from '#/components/Button/Button';
import { Icon } from '#/components/Icon/Icon';
import { H2 } from '#/components/Text/H2';
import { useI18n } from '#/i18n/hooks/useI18n';
import { Modal } from '#/modals/Modal';
import { listingApiFetchRsvps } from '#/modules/listing/events/api/listingApiFetchRsvps';
import type { ApiRsvp } from '#/modules/listing/events/api/types/ApiRsvpResponse';
import { viewUserProfilePath } from '#/modules/userProfile/routing/userProfile.paths';
import { colors } from '#/theme/theme';
import { EVENT_RSVP_STATUSES } from '#/utils/constants/general/eventRsvpStatuses';

const List = styled.ul`
  margin: 0;
  list-style: none;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: min(calc(100vh - 180px), 840px);
  padding: 0;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  align-content: space-between;
`;

const Left = styled.div`
  display: flex;
  color: ${colors.mediumContentGrey};
  font-weight: bold;
  font-size: 16px;
  align-items: center;
  flex-grow: 1;
  cursor: pointer;
`;

const Right = styled.div`
  display: flex;
  width: 150px;
`;

const TextInfo = styled.div`
  display: flex;
  flex-flow: column wrap;
  margin-left: 16px;
`;

const Status = styled.div<{
  $status: 'GOING' | 'INTERESTED' | 'NOT_GOING';
}>`
  text-transform: uppercase;
  color: white;
  font-size: 12px;
  border-radius: 8px;
  padding: 2px 5px;

  ${(props) => {
    switch (props.$status) {
      case 'GOING':
        return css`
          background-color: #9b489b;
        `;
      case 'INTERESTED':
        return css`
          background-color: ${colors.brandGreen};
        `;
      case 'NOT_GOING':
        return css`
          background-color: ${colors.mediumContentGrey};
        `;
    }
  }};
`;

type Props = {
  eventId: string;
  count: number;
  initialRsvps: ApiRsvp[];
  onClose: () => void;
};

export function EventShowAllRsvpsModal({
  eventId,
  count,
  initialRsvps,
  onClose,
}: Props) {
  const { i18n } = useI18n();

  const navigate = useNavigate();

  const [rsvps, setRsvps] = useState(initialRsvps);

  const title = (
    <H2>
      {count === 1
        ? i18n('%(count)s Person Responded', { count })
        : i18n('%(count)s People Responded', { count })}
    </H2>
  );

  const onLoadMore = () => {
    void listingApiFetchRsvps(eventId).then((allRsvps) => setRsvps(allRsvps));
  };

  return (
    <Modal
      title={title}
      onClose={onClose}
      wide
      qaId="event-show-all-rsvps-modal"
    >
      <List>
        {rsvps.map((rsvpResponse) => (
          <Row
            key={rsvpResponse.profile.username}
            data-qa-id="event-show-all-rsvps-modal-item"
          >
            <Left
              onClick={() => {
                void navigate(
                  viewUserProfilePath.with({
                    username: rsvpResponse.profile.username,
                  }),
                );
              }}
            >
              <Avatar
                image={rsvpResponse.profile.image}
                name={rsvpResponse.profile.name}
                size="sm"
              />
              <TextInfo>
                <div>{rsvpResponse.profile.name}</div>
                <div>
                  <Icon name="location" size={18} />
                  <span>
                    {rsvpResponse.profile.location
                      .replace(', United States', '')
                      .replace(', USA', '')}
                  </span>
                </div>
              </TextInfo>
            </Left>
            <Right>
              <Status $status={rsvpResponse.answer}>
                {EVENT_RSVP_STATUSES(i18n)[rsvpResponse.answer]}
              </Status>
            </Right>
          </Row>
        ))}
        {rsvps.length < count && (
          <Box
            component="nav"
            display="flex"
            width={1}
            alignItems="center"
            justifyContent="center"
            flex="1 1 auto"
          >
            <Button
              type="button"
              variant="secondary"
              qaId="event-show-all-rsvps-show-all"
              onClick={onLoadMore}
            >
              {i18n('Show all')}
            </Button>
          </Box>
        )}
      </List>
    </Modal>
  );
}
